import { useState } from 'react'
import type { ChecklistItemResponse, ChecklistSectionResponse } from '../api/types'
import { groupChecklist, matchesChecklistSearch, offeredChecklistItems } from '../lib/checklist'

/** Below this many items a search box is more furniture than help. */
const SEARCH_FROM = 8

/**
 * Pick the checklist items a file (or a batch of files) answers. Checkboxes drawn as chips;
 * the caller owns the selection. A terminal item (signed off, or not applicable) is offered
 * only when the document already carries it, so the link can be kept or dropped, never added:
 * the API refuses a new link under one (ADR 0007 s5), and the owner reopens it if a late file
 * belongs there.
 *
 * A flat row of chips was fine for a handful of items and is unusable at the Master DRL's 119,
 * so items are grouped by section and, past a handful, searchable by label ("2035"), title,
 * category and the request's own words. What is already picked stays visible whatever is typed,
 * so a search can never hide a selection from the person about to upload under it. A chip shows
 * the label and the title; the label alone would be a number to someone who has not memorised it.
 */
export function ChecklistPicker({
  items,
  sections = [],
  selected,
  onChange,
  legend = 'Answers checklist items',
}: {
  items: ChecklistItemResponse[]
  sections?: ChecklistSectionResponse[]
  selected: string[]
  onChange: (ids: string[]) => void
  legend?: string
}) {
  const [query, setQuery] = useState('')
  const offered = offeredChecklistItems(items, selected)
  if (offered.length === 0) return null

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])
  const shown = offered.filter((i) => selected.includes(i.id) || matchesChecklistSearch(i, query))
  const groups = groupChecklist(sections, shown).filter((g) => g.items.length > 0)
  const grouped = sections.length > 0

  return (
    <fieldset className="checklist-picker">
      <legend className="small muted">{legend}</legend>
      {offered.length >= SEARCH_FROM && (
        <input
          type="search"
          className="checklist-picker-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find an item by label, words or category"
          aria-label="Find a checklist item"
        />
      )}
      {groups.length === 0 && <p className="small muted no-margin">No item matches “{query}”.</p>}
      {groups.map((group) => (
        <div key={group.section?.id ?? 'uncategorized'} className="checklist-picker-group">
          {grouped && <p className="checklist-picker-heading">{group.name}</p>}
          <div className="chips">
            {group.items.map((item) => (
              <label
                key={item.id}
                className={`chip chip-pick${selected.includes(item.id) ? ' chip-picked' : ''}`}
                title={item.requestText ?? item.title}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(item.id)}
                  onChange={() => toggle(item.id)}
                />{' '}
                {item.docLabel !== null && <span className="mono">{item.docLabel} </span>}
                {item.title}
              </label>
            ))}
          </div>
        </div>
      ))}
    </fieldset>
  )
}
